import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export function lineAmount(l) { return (Number(l.unit_price) || 0) * (Number(l.qty) || 1); }
export function linesTotal(lines, rushFee = 0) { return lines.reduce((s, l) => s + lineAmount(l), 0) + Number(rushFee || 0); }

export function useCatalog(refreshKey) {
  const [catalog, setCatalog] = useState({});
  const [statePrices, setStatePrices] = useState({});
  useEffect(() => {
    if (refreshKey === false) return;
    api.get("/reports/catalog").then(({ data }) => setCatalog(data)).catch(() => {});
    api.get("/reports/catalog/state-prices").then(({ data }) => setStatePrices(data)).catch(() => {});
  }, [refreshKey]);
  return { catalog, statePrices };
}

export function priceFor(code, catalog, statePrices, state) {
  const item = catalog[code];
  if (!item) return 0;
  if (item.state_fee && state && statePrices[code]?.[state] != null) return statePrices[code][state];
  return Number(item.price) || 0;
}

export function LineItemsEditor({ lines, onChange, catalog, statePrices, driverState, testid = "line-items" }) {
  const codes = Object.keys(catalog).filter((k) => k !== "RUSH" && !k.startsWith("BGC"));
  const bgcCodes = Object.keys(catalog).filter((k) => k.startsWith("BGC"));
  const update = (i, patch) => onChange(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const remove = (i) => onChange(lines.filter((_, j) => j !== i));
  const mkLine = (code) => {
    const item = catalog[code];
    const state = item.state_fee ? (driverState || "") : undefined;
    return { code, label: item.label + (item.state_fee && state ? ` – ${state}` : ""), qty: 1, unit_price: priceFor(code, catalog, statePrices, state), state, per_unit: item.per_unit, state_fee: item.state_fee };
  };
  const addCatalog = (code) => {
    if (code === "BGC-BUNDLE") { onChange([...lines, ...bgcCodes.filter((c) => !lines.some((l) => l.code === c)).map(mkLine)]); return; }
    onChange([...lines, mkLine(code)]);
  };
  const addCustom = () => onChange([...lines, { code: null, label: "", qty: 1, unit_price: "" }]);

  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="grid grid-cols-[1fr_64px_96px_96px_28px] gap-2 tag-mono text-slate-500 px-1"><span>Item</span><span className="text-right">Qty</span><span className="text-right">Unit</span><span className="text-right">Amount</span><span /></div>
      {lines.length === 0 && <div className="text-xs text-slate-500 italic px-1">No line items yet — add a report below.</div>}
      {lines.some((l) => l.per_unit) && (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900" data-testid={`${testid}-bgc-scope`}>
          <span className="font-semibold">Background check scope</span>
          {["county", "state"].map((u) => { const idx = lines.findIndex((l) => l.per_unit === u); return idx < 0 ? null : (
            <label key={u} className="flex items-center gap-1">Total {u === "county" ? "counties" : "states"}
              <Input type="number" min="1" max="99" value={lines[idx].qty} className="h-7 w-16 font-mono text-right bg-white" onChange={(e) => update(idx, { qty: e.target.value })} data-testid={`${testid}-bgc-${u}-qty`} />
            </label>); })}
          <span className="text-sky-700">Totals update automatically · the invoice itemizes each county and state.</span>
        </div>
      )}
      {lines.map((l, i) => (
        <div key={i} className="grid grid-cols-[1fr_64px_96px_96px_28px] gap-2 items-center" data-testid={`${testid}-row-${i}`}>
          <div className="min-w-0">
            {l.code ? (
              <div className="flex items-center gap-2 min-w-0">
                <span className="tag-mono text-slate-500 shrink-0">{l.code}</span>
                {l.state_fee ? (
                  <Input value={l.state || ""} maxLength={2} placeholder="ST" className="h-8 w-14 font-mono uppercase" title="License state (sets the state fee)"
                    onChange={(e) => { const st = e.target.value.toUpperCase(); update(i, { state: st, label: `${catalog[l.code]?.label || l.label.split(" – ")[0]}${st ? ` – ${st}` : ""}`, unit_price: priceFor(l.code, catalog, statePrices, st) }); }} data-testid={`${testid}-state-${i}`} />
                ) : null}
                <span className="text-sm truncate">{l.label}{l.per_unit && <span className="text-slate-400"> · per {l.per_unit}</span>}</span>
              </div>
            ) : (
              <Input value={l.label} placeholder="Describe the service" className="h-8" onChange={(e) => update(i, { label: e.target.value })} data-testid={`${testid}-label-${i}`} />
            )}
          </div>
          <Input type="number" min="1" max="99" value={l.qty} className="h-8 font-mono text-right" onChange={(e) => update(i, { qty: e.target.value })} data-testid={`${testid}-qty-${i}`} />
          <Input type="number" step="0.01" min="0" value={l.unit_price} className="h-8 font-mono text-right" onChange={(e) => update(i, { unit_price: e.target.value, manual: true })} data-testid={`${testid}-unit-${i}`} />
          <div className="font-mono text-sm text-right" data-testid={`${testid}-amount-${i}`}>{money(lineAmount(l))}</div>
          <button type="button" className="text-slate-400 hover:text-rose-600" onClick={() => remove(i)} data-testid={`${testid}-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-2 pt-1">
        <Select value="" onValueChange={addCatalog}>
          <SelectTrigger className="h-8 w-64" data-testid={`${testid}-add-select`}><SelectValue placeholder="+ Add report from catalog" /></SelectTrigger>
          <SelectContent>
            {codes.map((k) => <SelectItem key={k} value={k}>{catalog[k].label}</SelectItem>)}
            {bgcCodes.length > 0 && <SelectItem value="BGC-BUNDLE" data-testid={`${testid}-add-bgc-bundle`}>Background Check Bundle (national + counties + states)</SelectItem>}
          </SelectContent>
        </Select>
        <Button type="button" size="sm" variant="outline" className="h-8" onClick={addCustom} data-testid={`${testid}-add-custom`}><Plus className="h-3.5 w-3.5 mr-1" />Custom line</Button>
      </div>
    </div>
  );
}

export function toLinePayload(lines) {
  // State-fee lines (MVR) let the server price from the live state-fee table unless the admin typed a price by hand
  return lines.map((l) => ({ code: l.code || null, label: l.label || null, qty: Number(l.qty) || 1, unit_price: l.unit_price === "" || l.unit_price == null || (l.state_fee && l.code && !l.manual) ? null : Number(l.unit_price), state: l.state || null }));
}
